import { useCallback, useEffect, useRef, useState } from "react";

type AmbientVideoProps = {
  src: string;
  /** Opcjonalne źródło WebM (podawane przed MP4). */
  webmSrc?: string;
  poster: string;
  /** Opcjonalny poster WebP (podawany przed JPG). */
  posterWebp?: string;
  alt: string;
  caption?: string;
  className?: string;
  /** Klasy dla samego materiału (np. proporcje i kadrowanie). */
  mediaClassName?: string;
  width?: number;
  height?: number;
};

/**
 * Ciche wideo w tle sekcji: autoplay, loop, bez kontrolek.
 * Plik wideo pobiera się dopiero po wejściu sekcji w viewport, a przy
 * prefers-reduced-motion domyślnie widoczny jest statyczny kadr (poster).
 * Użytkownik zawsze może włączyć lub wyłączyć ruch przełącznikiem.
 */
export function AmbientVideo({
  src,
  webmSrc,
  poster,
  posterWebp,
  alt,
  caption,
  className = "",
  mediaClassName = "aspect-[16/9] w-full object-cover",
  width,
  height,
}: AmbientVideoProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [nearViewport, setNearViewport] = useState(false);
  const [reduced, setReduced] = useState(true);
  /** null = jeszcze nie zdecydowano (idziemy za preferencją systemową) */
  const [userWants, setUserWants] = useState<boolean | null>(null);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const playing = userWants ?? !reduced;

  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setNearViewport(true);
            observer.disconnect();
          }
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const mounted = nearViewport && playing;

  useEffect(() => {
    if (!mounted) return;
    const node = videoRef.current;
    if (!node) return;
    const attempt = node.play();
    if (attempt) attempt.catch(() => undefined);
  }, [mounted]);

  const toggle = useCallback(() => {
    setUserWants((current) => !(current ?? !reduced));
  }, [reduced]);

  return (
    <figure className={className}>
      <div ref={wrapRef} className="photo-frame relative">
        {mounted ? (
          <video
            ref={videoRef}
            poster={posterWebp ?? poster}
            aria-label={alt}
            width={width}
            height={height}
            muted
            loop
            playsInline
            preload="metadata"
            className={mediaClassName}
          >
            {webmSrc ? <source src={webmSrc} type="video/webm" /> : null}
            <source src={src} type="video/mp4" />
          </video>
        ) : (
          <picture>
            {posterWebp ? <source srcSet={posterWebp} type="image/webp" /> : null}
            <img
              src={poster}
              alt={alt}
              width={width}
              height={height}
              loading="lazy"
              decoding="async"
              className={mediaClassName}
            />
          </picture>
        )}
        <button
          type="button"
          onClick={toggle}
          aria-pressed={playing}
          className="absolute bottom-3 right-3 rounded-sm bg-ink/75 px-3 py-1.5 text-xs font-medium tracking-wide text-surface backdrop-blur-sm transition-colors hover:bg-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-surface"
        >
          {playing ? "Zatrzymaj wideo" : "Odtwórz wideo"}
        </button>
      </div>
      {caption ? <figcaption className="photo-caption">{caption}</figcaption> : null}
    </figure>
  );
}
